import { BodyMetricType } from "../../../enums";

/** A measuring tape: a dashed line across the body part. */
const tape = (y: number, fromX: number, toX: number) => (
  <path d={`M${fromX} ${y}H${toX}`} strokeDasharray="2 1.6" />
);

/** A mannequin (head + torso) the girth icons share; only the height of the tape changes. */
const MANNEQUIN = (
  <>
    <circle cx="12" cy="3.8" r="2.1" />
    <path d="M7.5 8.2c1.4-.6 2.9-.9 4.5-.9s3.1.3 4.5.9c-.6 1.8-.8 3.4-.7 5 .1 1.4.7 2.8.7 4.4V21h-9v-3.4c0-1.6.6-3 .7-4.4.1-1.6-.1-3.2-.7-5z" />
  </>
);

const mannequin = (tapeY: number) => (
  <>
    {MANNEQUIN}
    {tape(tapeY, 5, 19)}
  </>
);

/** Hips and legs from the front; the thigh and calf icons put the tape on the left leg. */
const legs = (tapeY: number, toX: number) => (
  <>
    <path d="M6 3h12c.3 6-.2 12-1 18h-3.2l-1.3-11.5h-1L10.2 21H7c-.8-6-1.3-12-1-18z" />
    {tape(tapeY, 4.3, toX)}
  </>
);

const PATHS: Record<BodyMetricType, JSX.Element> = {
  [BodyMetricType.Weight]: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
      <path d="M8 10a4 4 0 0 1 8 0" />
      <path d="M12 10l1.8-2.4" />
      <path d="M9 16.5h6" />
    </>
  ),
  [BodyMetricType.Height]: (
    <>
      <circle cx="8" cy="5" r="2" />
      <path d="M8 8.5v6.5M5.5 21 8 15l2.5 6M5 11h6" />
      <path d="M18 3v18M16 5l2-2 2 2M16 19l2 2 2-2" />
    </>
  ),
  [BodyMetricType.BodyFat]: (
    <>
      <path d="M12 3c3.6 4.3 6.2 7.6 6.2 10.8a6.2 6.2 0 0 1-12.4 0C5.8 10.6 8.4 7.3 12 3z" />
      <path d="M14.2 12.2 9.8 17.8" />
      <circle cx="10" cy="12.8" r=".9" />
      <circle cx="14" cy="17.2" r=".9" />
    </>
  ),
  [BodyMetricType.MuscleMass]: (
    <>
      <path d="M6.5 6.5v11M17.5 6.5v11M3.5 9.5v5M20.5 9.5v5M6.5 12h11" />
    </>
  ),
  [BodyMetricType.BodyWater]: (
    <>
      <path d="M12 3c3.6 4.3 6.2 7.6 6.2 10.8a6.2 6.2 0 0 1-12.4 0C5.8 10.6 8.4 7.3 12 3z" />
      <path d="M8.3 14.6c1.2-.9 2.5-.9 3.7 0s2.5.9 3.7 0" />
    </>
  ),
  [BodyMetricType.VisceralFat]: (
    <>
      {MANNEQUIN}
      <circle cx="12" cy="14.2" r="2.5" />
      <circle cx="12" cy="14.2" r=".5" />
    </>
  ),
  [BodyMetricType.BoneMass]: (
    <path d="M17 10c.7-.7 1.7 0 2.5 0a2.5 2.5 0 1 0 0-5 .5.5 0 0 1-.5-.5 2.5 2.5 0 1 0-5 0c0 .8.7 1.8 0 2.5l-7 7c-.7.7-1.7 0-2.5 0a2.5 2.5 0 0 0 0 5c.3 0 .5.2.5.5a2.5 2.5 0 1 0 5 0c0-.8-.7-1.8 0-2.5z" />
  ),
  [BodyMetricType.RestingHeartRate]: (
    <>
      <path d="M19 14c1.5-1.5 3-3.2 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.8 0-3 .5-4.5 2-1.5-1.5-2.7-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4 3 5.5l7 7z" />
      <path d="M3.5 12h5.5l1-2 2 4.5 2-6 1.5 3.5h4.5" />
    </>
  ),
  [BodyMetricType.Waist]: mannequin(13.2),
  [BodyMetricType.Chest]: mannequin(10.2),
  [BodyMetricType.Hips]: mannequin(17.6),
  [BodyMetricType.Biceps]: (
    <>
      <path d="M12.4 13A5 5 0 0 1 22 15c0 3.9-4 7-9 7-4.1 0-8.2-.8-10.4-2.5-.4-.3-.6-.8-.6-1.3C2.1 12.7 2.6 2 10 2a3 3 0 0 1 3 3 2 2 0 0 1-2 2c-1.1 0-1.6-.4-2-1" />
      <path d="M15 14a5 5 0 0 0-7.6 2" />
      <path d="M10 6.8C8 8 9.5 13 8 15" />
    </>
  ),
  [BodyMetricType.Thigh]: legs(12.5, 12.7),
  [BodyMetricType.Calf]: legs(17.5, 12.2),
  [BodyMetricType.Neck]: (
    <>
      <circle cx="12" cy="5.6" r="3.1" />
      <path d="M10.3 8.6v4.6M13.7 8.6v4.6" />
      <path d="M4 21c0-4.1 3.6-7.6 8-7.6s8 3.5 8 7.6" />
      {tape(11, 7.5, 16.5)}
    </>
  ),
  [BodyMetricType.MetabolicAge]: (
    <>
      <path d="M6.5 3h11M6.5 21h11" />
      <path d="M8 3v2.6c0 1.2.5 2.3 1.4 3.1L12 11.3l2.6-2.6c.9-.8 1.4-1.9 1.4-3.1V3" />
      <path d="M8 21v-2.6c0-1.2.5-2.3 1.4-3.1L12 12.7l2.6 2.6c.9.8 1.4 1.9 1.4 3.1V21" />
      <path d="M10 18.2h4" />
    </>
  ),
  [BodyMetricType.Vo2Max]: (
    <>
      <path d="M12 3v5.5M10.4 8.5h3.2" />
      <path d="M10.4 8.5c-3.6 1.3-6.9 5-6.9 9.3 0 1.8 1.2 3 2.9 3 2.4 0 4-1.9 4-4.6z" />
      <path d="M13.6 8.5c3.6 1.3 6.9 5 6.9 9.3 0 1.8-1.2 3-2.9 3-2.4 0-4-1.9-4-4.6z" />
    </>
  ),
};

interface IBodyMetricIconProps {
  type: BodyMetricType;
  size?: number;
  className?: string;
}

/** Line icons for the body metrics, drawn in the current text color. */
export const BodyMetricIcon = ({ type, size = 24, className }: IBodyMetricIconProps) => {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[type]}
    </svg>
  );
};
